import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  TextField
} from "@mui/material";
import { SxProps } from "@mui/material/styles";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import InventoryOutlined from "@mui/icons-material/InventoryOutlined";
import { Close, Visibility } from "@mui/icons-material";
import { translate } from "../../translate/translate";
import { Value } from "./value-row-column.component";
import { parseLocaleNumber } from "../../utils/locale-number-utils";

const TableFixedResponsiveComponent = (props: any) => {
  const {
    displayedFixedColumnsShow,
    displayedFixedColumns,
    displayedCustomColumnsShow,
    displayedCustomColumns,
    renderCellHandler,
    headerCellCustomStyleHandler,
    cellCustomStyleHandler,
    rowCustomStyleHandler,
    sortingProps,
    displayedBalanceColumns,
    onClickValueForecast,
    onClickValueInTransit,
    onClickValueStock,
    onClickValueProjectedInventory,
  } = props;

  const { sortingColumns, onRequestSort, sortModel } = sortingProps || {};

  const table2Ref = useRef<HTMLDivElement | null>(null);

  const [containerWidth, setContainerWidth] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [showCopyMessage, setShowCopyMessage] = useState(false);

  const [, setModalType] = useState<"supplier" | "manufpn" | null>(null);
  const [modalTableData, setModalTableData] = useState<any[]>([]);
  const [openSupplierModal, setOpenSupplierModal] = useState(false);

  const [modalPartNumber, setModalPartNumber] = useState("");

  const handleOpenSupplierModal = (suppliers: any[], type: "supplier" | "manufpn", partNumber: string) => {
    setModalType(type);
    setModalPartNumber(partNumber);

    const formatted = suppliers.map((s: any) => ({
      name: s.name,
      pn: s.supplier_part_number
    }));

    setModalTableData(formatted);
    setOpenSupplierModal(true);
  };

  const handleCloseSupplierModal = () => {
    setOpenSupplierModal(false);
    setModalTableData([]);
    setModalType(null);
  };

  const handleOpenModal = (models: string[]) => {
    setSelectedModels(models);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedModels([]);
    setShowCopyMessage(false);
  };

  const handleCopyModels = async () => {
    try {
      const modelsString = selectedModels.join(", ");
      if (modelsString) {
        await navigator.clipboard.writeText(modelsString);
      }
      setShowCopyMessage(true);
    } catch (err) {
      toast.error(translate.t("copy_failed"));
    }
    setTimeout(() => {
      setShowCopyMessage(false);
    }, 2000);
  };

  const visibleColumns = displayedCustomColumns.length >= 8 ? 8 : displayedCustomColumns.length;

  useEffect(() => {
    const tableContainer = table2Ref.current;
    if (!tableContainer) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setContainerWidth(width);
    });

    observer.observe(tableContainer);
    return () => observer.disconnect();
  }, []);

  const formatDate = (dateStr: string) => {
    const date = dayjs(dateStr);

    if (date.isValid()) {
      const formattedDate = date.format("DD MMM");
      const parts = formattedDate.split(" ");
      if (parts.length > 1) {
        parts[1] = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      }
      return parts.join(" ").replace(/(\w{3})$/, "$1.");
    } else {
      return dateStr;
    }
  };

  const translateTitleColumn = (column: string, index: number): string => {

    const customNames: Record<string, string> = {
      supplier: translate.t("supplier"),
      manufacturer_part_number: translate.t("manufacturers"),
    };

    if (customNames[column]) {
      return customNames[column];
    }

    return displayedFixedColumnsShow === undefined || displayedFixedColumnsShow.length !== displayedFixedColumns.length
      ? translate.t(column)
      : translate.t(displayedFixedColumnsShow[index]);
  };

  const translateTitleColumnCustom = (column: string, isLastColumn: boolean): string => {
    const header = isLastColumn ? "total_to_go" : formatDate(column);
    return translate.t(header);
  };
  const translateRowColumn = (column: string, index: number, row: any): any => {

    if (column === "supplier") {
      const suppliers = Value(row, "suppliers");

      if (!Array.isArray(suppliers) || suppliers.length === 0) return "";

      const limited = suppliers.slice(0, 3);
      const isClickable = suppliers.length > 3;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            "&:hover .more-icon": { opacity: 0 },
            "&:hover .eye-icon": { opacity: 1 },
          }}
        >
          {limited.map((item: any, i: number) => (
            <span
              key={i}
              style={{ cursor: "pointer" }}
              onClick={() =>
                handleOpenSupplierModal(suppliers, "supplier", row.part_number)
              }
            >
              {item?.name ?? "-"}
              {i < limited.length - 1 && ","}
            </span>
          ))}

          {isClickable && (
            <Box
              onClick={() =>
                handleOpenSupplierModal(suppliers, "supplier", row.part_number)
              }
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                marginTop: 1,
                display: "inline-block",
                position: "relative",
              }}
            >
              <MoreHorizIcon className="more-icon" sx={{ color: "#90A4AE" }} />
              <VisibilityIcon
                className="eye-icon"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  color: "#90A4AE",
                }}
              />
            </Box>
          )}
        </Box>
      );
    }

    if (column === "manufacturer_part_number") {
      const suppliers = Value(row, "suppliers");

      if (!Array.isArray(suppliers) || suppliers.length === 0) return "";

      const limited = suppliers.slice(0, 3);
      const isClickable = suppliers.length > 3;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            "&:hover .more-icon": { opacity: 0 },
            "&:hover .eye-icon": { opacity: 1 },
          }}
        >
          {limited.map((item: any, i: number) => (
            <span
              key={i}
              onClick={() =>
                handleOpenSupplierModal(suppliers, "manufpn", row.part_number)
              }
              style={{
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "180px",
                color: "#45485F",
              }}
            >
              {item?.supplier_part_number ?? "-"}
              {i < limited.length - 1 && ","}
            </span>
          ))}

          {isClickable && (
            <Box
              onClick={() =>
                handleOpenSupplierModal(suppliers, "manufpn", row.part_number)
              }
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                marginTop: 1,
                display: "inline-block",
                position: "relative",
              }}
            >
              <MoreHorizIcon className="more-icon" sx={{ color: "#90A4AE" }} />
              <VisibilityIcon
                className="eye-icon"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  color: "#90A4AE",
                }}
              />
            </Box>
          )}
        </Box>
      );
    }

    if (column === "final_products") {
      const value = String(Value(row, column));
      const models = value.match(/[^,]+(?:\(\d+\))?/g) || [];
      const limitedModels = models.slice(0, 3);
      const isClickable = models.length > 3;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            "&:hover .more-icon": { opacity: 0 },
            "&:hover .eye-icon": { opacity: 1 },
          }}
        >
          {limitedModels.map((model, idx) => {
            const match = model.match(/^(.+?)\s*(\(\d+\))?$/);
            const name = match ? match[1].trim() : model;
            const qty = match && match[2] ? match[2] : "";

            return (
              <span
                key={idx}
                style={{
                  fontFamily: "Inter",
                  cursor: "pointer",
                  marginBottom: "4px",
                }}
                onClick={() => handleOpenModal(models)}
              >
                <span style={{ fontWeight: 400 }}>{name}</span>
                {qty && (
                  <span style={{ color: "#90A4AE", fontWeight: 600 }}>{qty}</span>
                )}
                {idx < limitedModels.length - 1 && ","}
              </span>
            );
          })}

          {isClickable && (
            <Box
              onClick={() => handleOpenModal(models)}
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                marginTop: 1,
                display: "inline-block",
                position: "relative",
              }}
            >
              <MoreHorizIcon className="more-icon" sx={{ color: "#90A4AE" }} />
              <VisibilityIcon
                className="eye-icon"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  color: "#90A4AE",
                }}
              />
            </Box>
          )}
        </Box>
      );
    }

    const raw = Value(row, column);
    if (raw === "" || raw === undefined || raw === null) return "";

    return raw;
  };


  const showPaginated = (from: number, to: number, count: number) => {
    return `${from} - ${to} ${translate.t("of")} ${count}`;
  };

  const isDisplayed = (column: string) => {
    return displayedBalanceColumns.length > 0 && displayedBalanceColumns.includes(column);
  };

  const formatValue = (value: number | null): string | null => {
    if (value === null || value === undefined) return null;
    return parseLocaleNumber(value);
  };

  // Calculate total width of fixed columns for scroll padding
  const calculateFixedColumnsWidth = (): number => {
    const columnWidths: { [key: string]: number } = {
      'part_number': 140,
      'mtype': 75,
      'description': 130,
      'final_products': 130,
      'forecast': 100,
      'stock': 95,
      'in_transit': 100,
      'waiting_invoice': 100,
      'waiting_invoice_label': 100,
      'balance': 110,
      'coverage': 155,
      'supplier': 145,
      'manufacturer_part_number': 190,
      'icons': 40
    };

    let totalWidth = 0;

    // Add width for each displayed fixed column (except icons)
    const nonIconColumns = displayedFixedColumnsShow.filter((col: any) => col !== "icons");
    for (const col of nonIconColumns) {
      totalWidth += columnWidths[col] || 120; // fallback width if not defined
    }

    // Add icons column width if it's displayed
    if (displayedFixedColumns.includes("icons")) {
      totalWidth += columnWidths['icons'];
    }

    return totalWidth;
  };

  // Calculate dynamic width for date columns - all columns same size for consistency
  const getDateColumnWidth = (isVisible: boolean, columnIndex: number): number => {
    if (containerWidth === 0) return 100; // fallback width

    const fixedColumnsWidth = calculateFixedColumnsWidth();
    const availableSpace = containerWidth - fixedColumnsWidth; // reduced margin to give more space to columns

    if (displayedCustomColumns.length <= 8) {
      // If 8 or fewer columns, distribute space equally with remainder distribution
      const baseWidth = Math.floor(availableSpace / displayedCustomColumns.length);
      const remainder = availableSpace % displayedCustomColumns.length;
      // Distribute remainder pixels to first columns to use all space exactly
      return baseWidth + (columnIndex < remainder ? 1 : 0);
    } else {
      // If more than 8 columns, calculate width for exactly 8 visible columns
      const baseWidth = Math.floor(availableSpace / 8);
      const remainder = availableSpace % 8;
      // Distribute remainder pixels to ensure exact space usage
      return baseWidth + (columnIndex < remainder ? 1 : 0);
    }
  };

  // Sticky column styles for unified table
  const getStickySx = (columnType: string, columnIndex: number, isHeader: boolean): SxProps => {
    const isLeftFixed = displayedFixedColumns.includes(columnType) || columnType === 'icons';
    let leftPos = 0;
    let columnWidth = 150;

    // Calculate sticky positions and widths for fixed columns based on the displayed fixed columns order
    const nonIconColumns = displayedFixedColumnsShow.filter((col: any) => col !== "icons");
    const columnIndexInFixed = nonIconColumns.indexOf(columnType);

    // Define widths for each possible column
    const columnWidths: { [key: string]: number } = {
      'part_number': 140,
      'mtype': 80,
      'description': 130,
      'final_products': 130,
      'forecast': 100,
      'stock': 100,
      'in_transit': 100,
      'waiting_invoice': 100,
      'waiting_invoice_label': 100,
      'balance': 110,
      'coverage': 150,
      'supplier': 140,
      'manufacturer_part_number': 190,
      'icons': 40
    };

    // Calculate position dynamically based on displayed columns
    if (columnType === 'icons') {
      // Icons column comes after all non-icon columns
      leftPos = 0;
      for (const col of nonIconColumns) {
        leftPos += columnWidths[col] || 120; // fallback width if not defined
      }
      columnWidth = columnWidths['icons'];
    } else if (columnIndexInFixed >= 0) {
      // Regular fixed column
      leftPos = 0;
      for (let i = 0; i < columnIndexInFixed; i++) {
        leftPos += columnWidths[nonIconColumns[i]] || 120; // fallback width
      }
      columnWidth = columnWidths[columnType] || 120; // fallback width
    }

    const sx: SxProps = {
      position: isLeftFixed ? 'sticky' : undefined,
      left: isLeftFixed ? leftPos : undefined,
      zIndex: isHeader ? (isLeftFixed ? 100 : 30) : (isLeftFixed ? 90 : 1),
      top: isHeader ? 0 : undefined,
      backgroundColor: isHeader
        ? (columnType === 'icons' ? '#fff' : (isLeftFixed ? '#3949AB' : '#fff'))
        : (isLeftFixed ? '#FAFAFA' : '#fff'),
      color: isHeader
        ? (columnType === 'icons' ? '#3949AB' : (isLeftFixed ? '#fff' : '#3949AB'))
        : '#45485F',
      fontWeight: isHeader ? 600 : 400,
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: isHeader ? 16 : 14,
      borderRight: isHeader
        ? (isLeftFixed ? 'none' : '1px solid #3949AB')
        : (columnType === 'icons' ? 'none' : '1px solid #ccc'),
      borderBottom: isHeader ? '1px solid #3949AB' : '1px solid rgba(128, 128, 128, 1)',
      padding: isHeader
        ? '10px 12px'
        : (columnType === 'icons' ? '0' : '8px 12px'),
      height: isHeader ? '56px' : '120px',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      boxSizing: 'border-box',
      minWidth: columnWidth,
      maxWidth: columnWidth,
      width: columnWidth,
    };

    return sx;
  };

  const SUBSTITUTE_TINT = {
    base: "#F3EAFB",
    sunken: "#EDE4F7",
    lastColumn: "#E7DAF5",
    hover: "#DFCFF3",
  };

  const balanceBackground = (row: any, columnIndex: number, band: "base" | "sunken") => {
    const isLastColumn = columnIndex === displayedCustomColumns.length - 1;

    if (row?.is_substitute) {
      return isLastColumn ? SUBSTITUTE_TINT.lastColumn : SUBSTITUTE_TINT[band];
    }

    if (isLastColumn) return "#ECEFF1";
    return band === "sunken" ? "#FAFAFA" : "#fff";
  };

  const balanceHoverBackground = (row: any) => (row?.is_substitute ? SUBSTITUTE_TINT.hover : "#ECEFF1");

  // Render date cell content with all 4 rows (forecast, in_transit, stock, projected_inventory)
  const renderDateCellContent = (row: any, date: string, columnIndex: number) => {
    const hasCellData = Boolean(row.dates[date]?.analysis_result_ids?.length);
    const hasInTransitDetail = (row.dates[date]?.in_transit ?? 0) !== 0;
    const hasStockDetail = (row.dates[date]?.stock ?? 0) !== 0;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {isDisplayed("forecast") && (
          <Box
            key={`forecast-${row.part_number || 'unknown'}-${date}-${columnIndex}`}
            sx={{
              flex: 1,
              display: "flex",
              borderBottom: "1px solid rgba(128, 128, 128, .2)",
              paddingRight: "4px",
              backgroundColor: balanceBackground(row, columnIndex, "base"),
              ...(hasCellData && {
                "&:hover": {
                  backgroundColor: balanceHoverBackground(row),
                  cursor: "pointer",
                  "& .visibility-icon": {
                    visibility: "visible",
                    opacity: 1,
                  },
                },
              }),
            }}
            onClick={() => {
              if (hasCellData) {
                onClickValueForecast(row?.part_number, row.dates[date]?.analysis_result_ids);
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "5px", width: '100%', paddingRight: '8px' }}>
              {hasCellData && (
                <Visibility
                  className="visibility-icon"
                  sx={{
                    color: "#90A4AE",
                    visibility: "hidden",
                    opacity: 0,
                    transition: "opacity 0.2s, visibility 0.2s",
                    fontSize: '16px',
                  }}
                />
              )}
              <span style={{ color: row.dates[date]?.forecast < 0 ? "#F44336" : "#45485F", fontSize: "14px", fontWeight: "400", fontFamily: "Inter" }}>
                {formatValue(row.dates[date]?.forecast)}
              </span>
            </Box>
          </Box>
        )}
        {isDisplayed("in_transit") && (
          <Box
            key={`in_transit-${row.part_number || 'unknown'}-${date}-${columnIndex}`}
            sx={{
              flex: 1,
              display: "flex",
              borderBottom: "1px solid rgba(128, 128, 128, .2)",
              paddingRight: "4px",
              backgroundColor: balanceBackground(row, columnIndex, "base"),
              ...(hasInTransitDetail && {
                "&:hover": {
                  backgroundColor: balanceHoverBackground(row),
                  cursor: "pointer",
                  "& .visibility-icon": {
                    visibility: "visible",
                    opacity: 1,
                  },
                }
              }),
            }}
            onClick={() => {
              if (hasInTransitDetail) {
                onClickValueInTransit(row?.part_number, row.dates[date]?.analysis_result_ids);
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "5px", width: '100%', paddingRight: '8px' }}>
              {hasInTransitDetail && (
                <Visibility
                  className="visibility-icon"
                  sx={{
                    color: "#90A4AE",
                    visibility: "hidden",
                    opacity: 0,
                    transition: "opacity 0.2s, visibility 0.2s",
                    fontSize: '16px',
                  }}
                />
              )}
              <span style={{ color: row.dates[date]?.in_transit < 0 ? "#F44336" : "#45485F", fontSize: "14px", fontWeight: "400", fontFamily: "Inter" }}>
                {formatValue(row.dates[date]?.in_transit)}
              </span>
            </Box>
          </Box>
        )}
        {isDisplayed("stock") && (
          <Box
            key={`stock-${row.part_number || 'unknown'}-${date}-${columnIndex}`}
            sx={{
              flex: 1,
              display: "flex",
              borderBottom: "1px solid rgba(128, 128, 128, .2)",
              paddingRight: "4px",
              backgroundColor: balanceBackground(row, columnIndex, "base"),
              ...(hasStockDetail && {
                "&:hover": {
                  backgroundColor: balanceHoverBackground(row),
                  cursor: "pointer",
                  "& .visibility-icon": {
                    visibility: "visible",
                    opacity: 1,
                  },
                }
              }),
            }}
            onClick={() => {
              if (hasStockDetail) {
                onClickValueStock(row?.part_number, row.dates[date]?.analysis_result_ids);
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "5px", width: '100%', paddingRight: '8px' }}>
              {hasStockDetail && (
                <Visibility
                  className="visibility-icon"
                  sx={{
                    color: "#90A4AE",
                    visibility: "hidden",
                    opacity: 0,
                    transition: "opacity 0.2s, visibility 0.2s",
                    fontSize: '16px',
                  }}
                />
              )}
              <span style={{ color: row.dates[date]?.stock < 0 ? "#F44336" : "#45485F", fontSize: "14px", fontWeight: "400", fontFamily: "Inter" }}>
                {formatValue(row.dates[date]?.stock)}
              </span>
            </Box>
          </Box>
        )}
        {isDisplayed("projected_inventory") && (
          <Box
            key={`projected_inventory-${row.part_number || 'unknown'}-${date}-${columnIndex}`}
            sx={{
              flex: 1,
              display: "flex",
              paddingRight: "4px",
              backgroundColor: balanceBackground(row, columnIndex, "sunken"),
              ...(hasCellData && {
                "&:hover": {
                  backgroundColor: balanceHoverBackground(row),
                  cursor: "pointer",
                  "& .visibility-icon": {
                    visibility: "visible",
                    opacity: 1,
                  },
                },
              }),
            }}
            onClick={() => {
              if (hasCellData) {
                onClickValueProjectedInventory(row?.part_number, row.dates[date]?.analysis_result_ids);
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "5px", width: '100%', paddingRight: '8px' }}>
              {hasCellData && (
                <Visibility
                  className="visibility-icon"
                  sx={{
                    color: "#90A4AE",
                    visibility: "hidden",
                    opacity: 0,
                    transition: "opacity 0.2s, visibility 0.2s",
                    fontSize: '16px',
                  }}
                />
              )}
              <span style={{ color: row.dates[date]?.projected_inventory < 0 ? "#F44336" : "#45485F", fontSize: "14px", fontWeight: "400", fontFamily: "Inter" }}>
                {formatValue(row.dates[date]?.projected_inventory)}
              </span>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Render icons column content
  const iconBandBackground = (row: any) => (row?.is_substitute ? SUBSTITUTE_TINT.base : "#FFF");

  const renderIconsColumnContent = (row: any) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
        {isDisplayed("forecast") && (
          <Box key="forecast-icon" sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: iconBandBackground(row), borderBottom: "1px solid rgba(128, 128, 128, 0.2)" }}>
            <Tooltip
              title={<Typography sx={{ fontSize: 14, color: "white" }}>{translate.t("forecast")}</Typography>}
              placement="top"
              sx={{ maxWidth: 205 }}
            >
              <CalendarMonthIcon sx={{ color: "#90a4ae", height: "23px", width: "23px" }} />
            </Tooltip>
          </Box>
        )}
        {isDisplayed("in_transit") && (
          <Box key="in_transit-icon" sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: iconBandBackground(row), borderBottom: "1px solid rgba(128, 128, 128, 0.2)" }}>
            <Tooltip
              title={<Typography sx={{ fontSize: 14, color: "white" }}>{translate.t("in_transit")}</Typography>}
              placement="top"
              sx={{ maxWidth: 205 }}
            >
              <LocalShippingIcon sx={{ color: "#90a4ae", height: "23px", width: "23px" }} />
            </Tooltip>
          </Box>
        )}
        {isDisplayed("stock") && (
          <Box key="stock-icon" sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: iconBandBackground(row), borderBottom: "1px solid rgba(128, 128, 128, 0.2)" }}>
            <Tooltip
              title={<Typography sx={{ fontSize: 14, color: "white" }}>{translate.t("in_stock")}</Typography>}
              placement="top"
              sx={{ maxWidth: 205 }}
            >
              <WarehouseIcon sx={{ color: "#90a4ae", height: "23px", width: "23px" }} />
            </Tooltip>
          </Box>
        )}
        {isDisplayed("projected_inventory") && (
          <Box key="projected_inventory-icon" sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: iconBandBackground(row) }}>
            <Tooltip
              title={<Typography sx={{ fontSize: 14, color: "white" }}>{translate.t("projected_inventory")}</Typography>}
              placement="top"
              sx={{ maxWidth: 205 }}
            >
              <InventoryOutlined sx={{ color: "#90a4ae", height: "23px", width: "23px" }} />
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{
          flex: 1,
          overflowX: "auto",
          height: "100%",
          // For screens showing more than 8 columns, create a viewport that shows exactly 8
          ...(displayedCustomColumns.length > 8 && {
            position: "relative",
            "& > div": {
              // Control the visible area to show exactly fixed columns + 8 date columns
              width: "100%",
              overflowX: "scroll"
            }
          })
        }}>
          <TableContainer
            sx={{
              maxHeight: "600px",
              overflowX: displayedCustomColumns.length > 8 ? "scroll" : "auto",
              scrollSnapType: displayedCustomColumns.length > 8 ? "x mandatory" : "none",
              scrollPadding: `0 0 0 ${calculateFixedColumnsWidth()}px`
            }}
            ref={table2Ref}
          >
            <Table stickyHeader sx={{
              minWidth: 1000,
              fontFamily: 'Inter, Arial, sans-serif',
              tableLayout: 'fixed'
            }}>
              <TableHead>
                <TableRow>
                  {/* Fixed columns headers */}
                  {displayedFixedColumnsShow.map((column: string, index: number) => {
                    // Skip the icons column in this loop since we handle it separately
                    if (column === "icons") return null;

                    const customStyle = headerCellCustomStyleHandler
                      ? headerCellCustomStyleHandler(column)
                      : {};

                    return (
                      <TableCell
                        key={`fixed-header-${column}-${index}`}
                        sx={{
                          ...getStickySx(column, index, true),
                          cursor: sortingColumns?.includes(column) ? "pointer" : "default",
                          // Override specific styles for fixed headers
                          textAlign: 'left',
                          ...customStyle,
                          lineHeight: '30px',                          fontSize: '16px !important',
                          fontWeight: '600 !important',
                          fontFamily: 'Inter !important',
                          color: 'white',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          }}                        sortDirection={
                          sortModel?.find((s: any) => s.column === column)?.direction || false
                        }
                      >
                        {sortingColumns && sortingColumns.includes(column) ? (
                          <TableSortLabel
                            active={!!sortModel?.find((s: any) => s.column === column)}
                            direction={
                              sortModel?.find((s: any) => s.column === column)?.direction || "asc"
                            }
                            onClick={() => onRequestSort(column)}
                            sx={{
                              "&:hover": { color: "inherit" },
                              "&.Mui-active": { color: "inherit" },
                              "& .MuiTableSortLabel-icon": { color: "white !important" },
                              "&.MuiTableSortLabel-root": { color: "inherit" },
                              // Apply same styles to sort label
                              textAlign: 'left',
                              lineHeight: '30px',
                              fontSize: '16px !important',
                              fontWeight: '600 !important',
                              fontFamily: 'Inter !important',
                              color: 'white',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              }}                          >
                            {translateTitleColumn(column, index)}
                          </TableSortLabel>
                        ) : (
                          translateTitleColumn(column, index)
                        )}
                      </TableCell>
                    );
                  })}

                  {/* Icons column header - only show if icons are in displayedFixedColumns */}
                  {displayedFixedColumns.includes("icons") && (
                    <TableCell
                      sx={{
                        ...getStickySx('icons', 999, true), // Use high index for icons
                      }}
                    >
                      {/* Empty header for icons column */}
                    </TableCell>
                  )}

                  {/* Dynamic date columns headers */}
                  {displayedCustomColumnsShow.map((column: string, index: number) => {
                    const isLastColumn = index === displayedCustomColumnsShow.length - 1;
                    const isVisible = index < visibleColumns;
                    const columnWidth = getDateColumnWidth(isVisible, index);
                    const customStyle = headerCellCustomStyleHandler
                      ? headerCellCustomStyleHandler(column)
                      : {};

                    return (
                      <TableCell
                        key={`date-${index}`}
                        sx={{
                          lineHeight: "30px",
                          fontSize: isLastColumn ? "14px !important" : "16px !important",
                          fontWeight: "600 !important",
                          fontFamily: "Inter !important",
                          backgroundColor: isLastColumn ? "#3949AB" : "white",
                          color: isLastColumn ? "white !important" : "#3949AB !important",
                          paddingTop: "10px",
                          paddingBottom: "10px",
                          borderRight: isLastColumn ? "1px solid #ccc" : "1px solid #3949AB", // Last column: gray border
                          borderLeft: isLastColumn ? "1px solid #3949AB" : "none", // Last column: blue left border
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          textAlign: isLastColumn ? "center" : "right",
                          position: "sticky",
                          top: 0,
                          zIndex: 30,
                          paddingRight: "5px !important",
                          height: "56px",
                          borderBottom: "1px solid #3949AB",
                          minWidth: columnWidth,
                          maxWidth: columnWidth,
                          width: columnWidth,
                          scrollSnapAlign: "start", // Only apply snap to date columns
                          ...customStyle
                        }}
                      >
                        {translateTitleColumnCustom(column, isLastColumn)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {props.state && props.state.length > 0 ? (
                  props.state.map((row: any, indexRow: number) => (
                    <TableRow key={`row-${row.part_number || 'unknown'}-${indexRow}`} sx={{ ...rowCustomStyleHandler ? rowCustomStyleHandler(row) : {} }}>
                      {/* Fixed columns content */}
                      {displayedFixedColumns.map((column: string, index: number) => {
                        // Skip the icons column in this loop since we handle it separately
                        if (column === "icons") return null;

                        return (
                          <TableCell
                            key={`fixed-body-${column}-${indexRow}-${index}`}
                            sx={{
                              ...getStickySx(column, index, false),
                              wordBreak: "break-word",
                              borderRight: "1px solid #ccc",
                              borderBottom: indexRow === props.state.length - 1 ? "none" : "1px solid rgba(128, 128, 128, 1)",
                              whiteSpace: "normal",
                              textOverflow: "ellipsis",
                              ...cellCustomStyleHandler ? cellCustomStyleHandler(column, row) : {},                            }}
                          >
                            {renderCellHandler && renderCellHandler[column]
                              ? renderCellHandler[column](column, index, row)
                              : translateRowColumn(column, index, row)}
                          </TableCell>
                        );
                      })}

                      {/* Icons column - only show if icons are in displayedFixedColumns */}
                      {displayedFixedColumns.includes("icons") && (
                        <TableCell
                          sx={{
                            ...getStickySx('icons', 999, false), // Use high index for icons
                            borderRight: 0,
                            borderBottom: indexRow === props.state.length - 1 ? "none" : "1px solid rgba(128, 128, 128, 1)",
                            ...cellCustomStyleHandler ? cellCustomStyleHandler('icons', row) : {},
                          }}
                        >
                          {renderIconsColumnContent(row)}
                        </TableCell>
                      )}
                      {/* Dynamic date columns content - show all columns with horizontal scroll */}
                      {displayedCustomColumns.map((date: string, index: number) => {
                        const isLastColumn = index === displayedCustomColumns.length - 1;
                        const isVisible = index < visibleColumns;
                        const columnWidth = getDateColumnWidth(isVisible, index);

                        return (
                          <TableCell
                            key={`date-content-${index}`}
                            sx={{
                              borderBottom: indexRow === props.state.length - 1 ? "none" : "1px solid rgba(128, 128, 128, 1)",
                              borderRight: isLastColumn ? "1px solid #ccc" : "1px solid #ccc", // Last column: gray border
                              borderLeft: isLastColumn ? "1px solid #3949AB" : "none", // Last column: blue left border
                              padding: 0,
                              height: "120px",
                              textAlign: "right",
                              minWidth: columnWidth,
                              maxWidth: columnWidth,
                              width: columnWidth,
                              scrollSnapAlign: "start", // Only apply snap to date columns
                            }}
                          >
                            {renderDateCellContent(row, date, index)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={displayedFixedColumnsShow.filter((col: any) => col !== "icons").length + (displayedFixedColumns.includes("icons") ? 1 : 0) + displayedCustomColumns.length}
                      sx={{ textAlign: "center", fontSize: "14px", padding: "8px" }}
                    >
                      {translate.t("empty_listing")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <TablePagination
        sx={{ color: "#45485F", textAlign: "end", minHeight: "52px", maxHeight: "52px" }}
        component="div"
        rowsPerPageOptions={[5, 10, 15, 25, 50]}
        colSpan={12}
        count={Number(props.count ?? 0)}
        rowsPerPage={props.rowsPerPage}
        page={props.page}
        onPageChange={props.onPageChange}
        onRowsPerPageChange={props.handleChangeRowsPerPage}
        ActionsComponent={props.actionsComponent}
        labelRowsPerPage={translate.t("rows_per_page")}
        labelDisplayedRows={({ from, to, count }) => showPaginated(from, to, count)}
      />
      <Dialog open={openModal} onClose={handleCloseModal}>
        <Grid
          padding={2}
          container
          justifyContent="space-between"
          sx={{ backgroundColor: "primary.main" }}
        >
          <Grid item>
            <Typography
              sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}
            >
              {translate.t("models")}
            </Typography>
          </Grid>
          <Grid item sx={{ cursor: "pointer", color: "#ffffff" }}>
            <Close onClick={handleCloseModal} />
          </Grid>
        </Grid>
        <DialogContent sx={{ minWidth: "500px" }}>
          <Grid container flexDirection="column" gap={1}>
            <Grid item>
              <Typography>
                <b>{translate.t("model_quantity")}:</b> {selectedModels.length}
              </Typography>
            </Grid>

            <Grid item>
              <Grid sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography>
                  <b>{translate.t("models")}:</b>
                </Typography>

                <Grid
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    gap: "2px",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease, transform 0.2s ease",
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                    "&:active": {
                      backgroundColor: (theme) => theme.palette.action.selected,
                    },
                  }}
                  onClick={handleCopyModels}
                >
                  <ContentCopyOutlinedIcon fontSize={"small"} color={"primary"} />
                  <Typography color={"primary"}>
                    <b>{translate.t(showCopyMessage ? "copied" : "copy")}</b>
                  </Typography>
                </Grid>
              </Grid>

              <Grid sx={{
                "& .MuiInputBase-root": {
                  padding: "5px 0px 5px 5px",
                },
                marginTop: "10px",
              }}>
                <TextField
                  multiline
                  rows={7}
                  value={selectedModels.join(", ")}
                  fullWidth
                  disabled={true}
                  sx={{
                    "& .MuiInputBase-input": {
                      "&[disabled]": {
                        color: "#607883",
                        WebkitTextFillColor: "#607883",
                      },
                    },
                    backgroundColor: "#FAFAFA",
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="flex-end" gap={1} padding={2}>
            <Grid item>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleCloseModal}
              >
                {translate.t("close")}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openSupplierModal}
        onClose={handleCloseSupplierModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "10px", maxWidth: 400 }
        }}
      >
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          padding={2}
          sx={{ backgroundColor: "#303F9F" }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            {translate.t("part_number")}
            {" - "}
            {modalPartNumber}
          </Typography>

          <Close
            onClick={handleCloseSupplierModal}
            sx={{ cursor: "pointer", color: "#fff" }}
          />
        </Grid>
        <DialogContent
          sx={{
            padding: 3,
            backgroundColor: "#ffffff"
          }}
        >
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: "8px",
              overflow: "hidden",
              border: "1px solid #DDE1EB",
              maxWidth: 400
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#F4F5F7",
                    "& th": {
                      backgroundColor: "#F4F5F7",
                      color: "#303F9F !important",
                      WebkitTextFillColor: "#303F9F !important",
                      fontWeight: 600,
                      fontSize: "15px",
                      borderBottom: "1px solid #E3E5EC",
                      paddingY: 1.5
                    }
                  }}
                >
                  <TableCell>{translate.t("supplier")}</TableCell>
                  <TableCell>{translate.t("manufacturer")}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {modalTableData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      height: "45px",
                      "&:hover": { backgroundColor: "#F0F7FF" }
                    }}
                  >
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.pn}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ padding: "16px", backgroundColor: "#ffffff" }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCloseSupplierModal}
          >
            {translate.t("close")}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default TableFixedResponsiveComponent;
